import { TeachingLoadReportType } from '@bp/planung-graphql-types';
import {
  AddIcon,
  Button,
  ButtonGroup,
  DeleteIcon,
  EmptyState,
  ImportIcon,
  NoTasksIcon,
  Table,
  TableColumns,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { SortDirection, useTeachingLoadReportsQuery } from '../../../types/planung-graphql-client-defs';
import {
  createBerlinExcelReport,
  createTeachingLoadExcelReport,
  createTeachingLoadLelisteReport,
  deleteExcelReport,
  downloadExcelReport,
} from '../../../utils/excelReport';
import { createTeachingLoadReport, deleteReport, downloadReport } from '../../../utils/pdfReport';

type TeachingLoadReportsTableType = {
  uuid: string;
  name: string;
  contextUuid: string;
  contextName: string;
  timestamp: dayjs.Dayjs;
};

type TeachingLoadReportsTableProps = {
  showEmptyState: boolean;
  subType: TeachingLoadReportType;
  timetableBlockUuid: string;
};

export const TeachingLoadReportsTable = observer(
  ({ showEmptyState, subType, timetableBlockUuid }: TeachingLoadReportsTableProps) => {
    const { t } = useTranslation();
    const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
    const schoolYearUuid = currentSchoolYear?.uuid;

    const [loading, setLoading] = useState<string | 'all' | null>(null);

    const { ConfirmationDialog, confirm } = useConfirm({
      defaultMessage: t('delete.message', { context: 'female', count: 1, type: t('pdf.title', { count: 1 }) }),
    });

    const [{ data: teachingLoadReportsData }, refetchReports] = useTeachingLoadReportsQuery({
      variables: { where: {}, options: { sort: [{ jobCreated: SortDirection.Desc }] } },
    });

    const columns: TableColumns<TeachingLoadReportsTableType>[] = [
      {
        header: '',
        id: 'name',
        accessorKey: 'name',
        size: 480,
      },
      {
        header: '',
        id: 'timestamp',
        accessorFn: (row) => {
          return row.timestamp.format('DD.MM.YY HH:mm');
        },
        size: 120,
      },
    ];

    const data: TeachingLoadReportsTableType[] =
      teachingLoadReportsData?.teachingLoadReports
        .filter((r) => {
          return (
            (r.context.__typename !== 'SchoolYear' && timetableBlockUuid) ||
            (r.context.__typename === 'SchoolYear' && timetableBlockUuid === 'all')
          );
        })
        .filter((r) => {
          if (r.context.__typename === 'SchoolYear' && schoolYearUuid) {
            return r.type === subType && r.context.uuid === schoolYearUuid;
          }
          if (
            r.context.__typename === 'TeachingBlock' ||
            (r.context.__typename === 'TimetableBlock' && schoolYearUuid)
          ) {
            return (
              r.type === subType &&
              r.context.uuid === timetableBlockUuid &&
              r.context.schoolYear.uuid === schoolYearUuid
            );
          }
          return false; // show only if the report has context
        })
        .map((report) => {
          return {
            uuid: report.uuid,
            name: report.title,
            contextUuid: report.context.uuid,
            contextName: report.context.name,
            timestamp: dayjs(report.jobFinished),
          };
        }) ?? [];

    async function handleDownload(report: TeachingLoadReportsTableType) {
      if (loading) return;
      setLoading(report.uuid);
      if (
        subType === TeachingLoadReportType.PlanExcel ||
        subType === TeachingLoadReportType.CurrentExcel ||
        subType === TeachingLoadReportType.PlanLeliste ||
        subType === TeachingLoadReportType.CurrentLeliste ||
        subType === TeachingLoadReportType.PlanExcelBerlin ||
        subType === TeachingLoadReportType.CurrentExcelBerlin
      ) {
        await downloadExcelReport(report, subType);
      } else {
        void (await downloadReport(report, subType));
      }
      setLoading(null);
    }

    async function handleDelete(report: TeachingLoadReportsTableType) {
      if (loading) return;
      const result = await confirm();
      if (!result) return;
      setLoading(report.uuid);
      if (
        subType === TeachingLoadReportType.PlanExcel ||
        subType === TeachingLoadReportType.CurrentExcel ||
        subType === TeachingLoadReportType.PlanLeliste ||
        subType === TeachingLoadReportType.CurrentLeliste ||
        subType === TeachingLoadReportType.PlanExcelBerlin ||
        subType === TeachingLoadReportType.CurrentExcelBerlin
      ) {
        await deleteExcelReport(report, subType);
      } else {
        await deleteReport(report, subType);
      }
      refetchReports({ requestPolicy: 'network-only' });
      setLoading(null);
    }

    async function handleGenerate() {
      if (loading) return;
      setLoading('all');

      if (subType === TeachingLoadReportType.PlanExcel) {
        // TODO fix in BE. add subType to excel route !
        const _timetableBlockUuid = timetableBlockUuid === 'all' ? 'default' : timetableBlockUuid;
        await createTeachingLoadExcelReport(currentSchoolYear?.uuid ?? '', _timetableBlockUuid);
      } else if (subType === TeachingLoadReportType.CurrentExcel) {
        await createTeachingLoadExcelReport(currentSchoolYear?.uuid ?? '', timetableBlockUuid ?? 'all');
      } else if (subType === TeachingLoadReportType.CurrentLeliste) {
        await createTeachingLoadLelisteReport(currentSchoolYear?.uuid ?? '', timetableBlockUuid ?? 'all');
      } else if (subType === TeachingLoadReportType.PlanLeliste) {
        await createTeachingLoadLelisteReport(currentSchoolYear?.uuid ?? '', 'default');
      } else if (
        subType === TeachingLoadReportType.PlanExcelBerlin ||
        subType === TeachingLoadReportType.CurrentExcelBerlin
      ) {
        await createBerlinExcelReport(currentSchoolYear?.uuid ?? '', timetableBlockUuid ?? 'all');
      } else {
        await createTeachingLoadReport(
          currentSchoolYear?.uuid ?? '',
          !timetableBlockUuid || timetableBlockUuid === '' ? 'default' : timetableBlockUuid,
          'teaching-load',
          subType,
        );
      }
      refetchReports({ requestPolicy: 'network-only' });
      setLoading(null);
    }

    return (
      <>
        {!showEmptyState ? (
          <Table<TeachingLoadReportsTableType>
            data={data}
            columns={columns}
            hideHeader
            showActionBar
            actionBarSettings={{
              globalFilterPlaceholder: t('common.search'),
              extendedActionsRight: (
                <Button isLoading={loading === 'all'} icon={<AddIcon />} onClick={handleGenerate}>
                  {t('common.generate')}
                </Button>
              ),
            }}
            emptyStateSettings={{ size: 'small', hideIcon: true, subtitle: '', title: t('pdf.noPdfFound') }}
            customPadding='var(--spacing-6)'
            lastColWidth='69px'
            lastCol={({ original }) => (
              <ButtonGroup loading={loading === original.uuid}>
                <Button
                  hierarchy='secondary'
                  icon={<ImportIcon className='small' />}
                  onClick={() => handleDownload(original)}
                />
                <Button
                  hierarchy='secondary'
                  icon={<DeleteIcon className='small' />}
                  onClick={() => handleDelete(original)}
                />
              </ButtonGroup>
            )}
          />
        ) : (
          <EmptyState title={t('common.commingSoon')} icon={<NoTasksIcon />} padding='l' />
        )}

        <ConfirmationDialog />
      </>
    );
  },
);
